import React, { useState } from "react";
import { sendInstallEmail } from "../lib/pushClient";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { MAC_DOWNLOAD_URL } from "./DesktopInstallBanner";
import "./DesktopCompanionModal.css";
import BrandLogo from "./BrandLogo";
import { t } from "../i18n";
import { T } from "../i18n/T";

interface Props {
  onDismiss: () => void;
}

// "Download the Desktop Companion" pop-up. Fires once per account, right
// after the DJ handles the DEMO request on the Requests page (see
// RequestsPage.queueDesktopPrompt) for EVERY DJ, US and non-US. The
// Desktop Companion is the primary, most reliable way to receive paid-request
// alerts.
//
// Platform-aware: on a laptop we offer the direct Mac download (+ a Windows
// install note). On a phone you can't install a desktop app, so the button
// emails the download link on demand (POST /push/send-install-email) and
// flips to "Sent ✓" — the DJ asked for it, so it's never a surprise email.
export default function DesktopCompanionModal({ onDismiss }: Props) {
  const { isDesktop } = useInstallPrompt();
  const [emailState, setEmailState] = useState<"idle" | "sending" | "sent" | "error">("idle");

  // Inside the Electron desktop companion itself, advertising the desktop
  // companion is nonsense (founder bug report: the download pop-up
  // appeared IN the app). Same guard as DesktopInstallBanner.
  if ((window as any).electronAPI?.isElectron) return null;

  const sendEmail = async () => {
    if (emailState === "sending" || emailState === "sent") return;
    setEmailState("sending");
    try {
      await sendInstallEmail();
      setEmailState("sent");
    } catch {
      setEmailState("error");
    }
  };

  return (
    <div
      className="dc-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t("desktopCompanionModal.getTheDesktopCompanion")}
      onClick={onDismiss}
    >
      <div className="dc-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="dc-close" aria-label={t("desktopCompanionModal.close")} onClick={onDismiss}>
          &#10005;
        </button>

        {/* Founder's simplified layout: headline → icon →
            DESKTOP COMPANION label → one proof line → card → email note. */}
        <h3 className="dc-title">{t("desktopCompanionModal.neverMissARequest")}</h3>

        <div className="dc-icon-wrap">
          <div className="dc-icon">
            <BrandLogo variant="icon" alt={t("desktopCompanionModal.requestApp")} />
          </div>
          <span className="dc-badge" aria-hidden>
            5
          </span>
        </div>

        <div className="dc-eyebrow dc-eyebrow-below">
          {/* Laptop line icon (no emoji in product UI — brand rule). */}
          <svg
            className="dc-eyebrow-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <rect x="3" y="5" width="18" height="12" rx="2" />
            <path d="M1 19h22" />
          </svg>
          {t("desktopCompanionModal.desktopCompanion")}
        </div>
        <p className="dc-sub">{t("desktopCompanionModal.worksWithSeratoRekordbox")}</p>

        {/* Illustrative sample request card — mirrors the real OfferCard look.
            Inert: the buttons are non-interactive (rendered as spans). */}
        <div className="dc-card" aria-hidden>
          <span className="dc-card-time">{t("desktopCompanionModal.justNow")}</span>
          <div className="dc-card-left">
            <img className="dc-card-art" src="/sample-request-cover.jpg" alt="" />
            <div className="dc-card-details">
              <div className="dc-card-title">{t("desktopCompanionModal.september")}</div>
              <div className="dc-card-artist">{t("desktopCompanionModal.earthWindFire")}</div>
              <div className="dc-card-amount">$20.00</div>
            </div>
          </div>
          <div className="dc-card-actions">
            <span className="dc-card-btn dc-card-accept">
              {t("desktopCompanionModal.accept")}
              {/* Mouse cursor mid-hover — the desktop tell. Shape traced from
                  the classic macOS arrow (vertical left edge + click stem). */}
              <svg className="dc-cursor" viewBox="-1.6 -1.6 17.6 22.6" aria-hidden>
                <path
                  d="M0 0 L0 16.9 L4.6 12.7 L7.6 19.4 L11 17.9 L8 11.4 L14.4 11.4 Z"
                  fill="#000"
                  stroke="#fff"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="dc-card-btn dc-card-decline">{t("desktopCompanionModal.decline")}</span>
          </div>
        </div>

        {isDesktop ? (
          <>
            <a className="dc-primary" href={MAC_DOWNLOAD_URL} download>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
                <path d="M7 11l5 5 5 -5" />
                <path d="M12 4v12" />
              </svg>
              {t("desktopCompanionModal.downloadForMac")}
            </a>
            <p className="dc-win-note">
              <T k="desktopCompanionModal.onWindowsOpenWww" tags={{ 0: (c) => <a href="https://www.requesta.app" target="_blank" rel="noopener noreferrer">{c}</a> }} />
            </p>
          </>
        ) : (
          <>
            <button
              type="button"
              className={`dc-primary dc-primary-green${emailState === "sent" ? " dc-primary-sent" : ""}`}
              onClick={sendEmail}
              disabled={emailState === "sending" || emailState === "sent"}
            >
              {emailState === "sent" ? t("desktopCompanionModal.sent") : emailState === "sending" ? t("desktopCompanionModal.sending") : t("desktopCompanionModal.sendToMyEmail")}
            </button>
            {emailState === "error" && <p className="dc-win-note dc-error">{t("desktopCompanionModal.couldnTSendTry")}</p>}
          </>
        )}
      </div>
    </div>
  );
}
